/**
 * One category on one clip or subclip, edited from its cell in Tagging's Browse tab: every value the brand offers
 * for that category as a pill. A click on a grey pill applies it (blue); the × on a blue pill takes it
 * off again. Each click saves at once, the way every tag in Tagging does; on a clip it lands on the
 * clip's subclips too, as the asset's tags always do. Owners get a gear to the category in Brand settings.
 */
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/icons';
import { queryClient, trpc } from '@/lib/trpc';

export interface HeldValue { valueId: string; label: string; tagIds: string[] }
export interface OfferedValue { id: string; label: string; current: boolean }
/** The clip or subclip a cell belongs to; the name heads the popup. */
export interface EditTarget { kind: 'asset' | 'snippet'; id: string; name: string }

export function CellTagsModal({ target, facet, held, offered, canConfigure, onClose }: {
  target: EditTarget;
  facet: { id: string; key: string; label: string };
  /** What the clip carries in this category now. */
  held: HeldValue[];
  /** The brand's values for this category, alphabetical. */
  offered: OfferedValue[];
  canConfigure: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  /** A click shows at once; the override goes when the table's refetch carries the same answer. */
  const [pending, setPending] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const settle = async (valueId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.tags.pathKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.facets.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() }),
    ]);
    setPending((p) => {
      const next = new Map(p);
      next.delete(valueId);
      return next;
    });
  };
  const apply = useMutation(trpc.tags.applyTag.mutationOptions());
  const remove = useMutation(trpc.tags.remove.mutationOptions());

  const heldById = new Map(held.map((h) => [h.valueId, h]));
  const isOn = (valueId: string) => pending.get(valueId) ?? heldById.has(valueId);
  // A value no longer offered still shows while the clip carries it, so it can be taken off.
  const shown = offered.filter((v) => v.current || heldById.has(v.id));

  const toggle = async (v: OfferedValue, on: boolean) => {
    setPending((p) => new Map(p).set(v.id, on));
    try {
      if (on) await apply.mutateAsync({ targetKind: target.kind, targetId: target.id, facet: { key: facet.key, value: v.label }, source: 'user' });
      else await Promise.all((heldById.get(v.id)?.tagIds ?? []).map((tagId) => remove.mutateAsync({ tagId })));
    } finally {
      await settle(v.id);
    }
  };

  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal cell-tags-modal" role="dialog" aria-label={`${facet.label} for ${target.name}`}>
        <button type="button" className="modal-x" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        <div className="row cell-tags-head">
          <h2>{facet.label}</h2>
          {canConfigure && (
            <button type="button" className="icon-btn cell-tags-gear" title={`Edit the ${facet.label} options in Brand settings`}
              aria-label={`Edit the ${facet.label} options in Brand settings`}
              onClick={() => void navigate({ to: '/settings', search: { category: facet.id } })}>
              <Icon name="gear" />
            </button>
          )}
        </div>
        <span className="sub cell-tags-clip">{target.name}</span>
        <div className="tag-wrap">
          {shown.map((v) => isOn(v.id) ? (
            <span key={v.id} className="tg">
              {v.label}
              <button type="button" className="tg-x" aria-label={`Remove ${v.label}`} onClick={() => void toggle(v, false)}>×</button>
            </span>
          ) : (
            <button key={v.id} type="button" className="tg sug" onClick={() => void toggle(v, true)}>{v.label}</button>
          ))}
          {shown.length === 0 && <span className="sub">No options in this category yet.</span>}
        </div>
        {(apply.isError || remove.isError) && <div className="error" role="alert">{apply.error?.message ?? remove.error?.message}</div>}
      </div>
    </>
  );
}
